import { Separator } from "@/components/ui/separator";

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">The fine print</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            PRIVACY POLICY
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
        </div>
      </section>

      {/* Content */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="card-brutal bg-white p-8 lg:p-12">
            <p className="font-outfit text-sm text-ink/60">Last updated: February 2026</p>
            <p className="mt-4 font-courier text-sm text-ink/70 leading-relaxed">
              This Privacy Policy applies to <strong>seasthebay.org</strong> (operated by Seas The Bay)
              and the HostBase guest portal at <strong>hostbase.app</strong>.
            </p>

            <Separator className="my-8 bg-ink/10" />

            <div className="space-y-8">
              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">1. INFORMATION WE COLLECT</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  When you use the seasthebay.org website or the HostBase guest portal, we collect the following information:
                </p>
                <ul className="mt-3 space-y-2 pl-4">
                  <li className="font-courier text-sm text-ink/70 leading-relaxed">
                    <strong>Phone Number</strong> — Used for account verification via one-time passcode (OTP) and to send order confirmations.
                  </li>
                  <li className="font-courier text-sm text-ink/70 leading-relaxed">
                    <strong>Name</strong> — Optionally provided by you or the property owner to personalize your experience.
                  </li>
                  <li className="font-courier text-sm text-ink/70 leading-relaxed">
                    <strong>Stay Dates</strong> — Check-in and check-out dates associated with your reservation.
                  </li>
                  <li className="font-courier text-sm text-ink/70 leading-relaxed">
                    <strong>Order Information</strong> — Details of services and products you purchase through the platform, including payment transaction records.
                  </li>
                  <li className="font-courier text-sm text-ink/70 leading-relaxed">
                    <strong>Usage Data</strong> — Pages viewed, features used, and general interaction data to improve the platform.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">2. HOW WE USE YOUR INFORMATION</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We use the information we collect to:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">Verify your identity and grant access to your property's guest portal</li>
                  <li className="font-courier text-sm text-ink/70">Process orders for services and products</li>
                  <li className="font-courier text-sm text-ink/70">Send SMS confirmations for authentication and order updates</li>
                  <li className="font-courier text-sm text-ink/70">Personalize your guest experience</li>
                  <li className="font-courier text-sm text-ink/70">Improve and optimize the platform</li>
                  <li className="font-courier text-sm text-ink/70">Comply with legal obligations</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">3. SMS COMMUNICATIONS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  By providing your phone number, you consent to receive SMS messages for: verification codes (OTP), order confirmations, and property-related updates. Message and data rates may apply. Message frequency varies. You can opt out at any time by contacting the property manager at seasthebay.org or through the HostBase portal.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">4. PAYMENT INFORMATION</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  Payment processing is handled securely by Stripe. We do not store your credit card number, CVV, or full payment details on our servers. Stripe's privacy policy governs the handling of your payment information.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">5. DATA SHARING</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="mt-3 space-y-2 pl-4">
                  <li className="font-courier text-sm text-ink/70">
                    <strong>Property Owners/Managers</strong> — To fulfill services and manage your stay at Seas The Bay
                  </li>
                  <li className="font-courier text-sm text-ink/70">
                    <strong>Service Providers</strong> — Third-party vendors who help operate the platform (Twilio for SMS, Stripe for payments)
                  </li>
                  <li className="font-courier text-sm text-ink/70">
                    <strong>Legal Requirements</strong> — When required by law or to protect our rights
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">6. DATA RETENTION</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We retain your personal information for as long as necessary to provide our services and fulfill the purposes described in this policy. Order records are retained for accounting and legal compliance purposes. You may request deletion of your data by contacting us.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">7. SECURITY</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We implement reasonable security measures to protect your information, including encrypted data transmission (HTTPS), secure authentication tokens, and access controls. However, no method of transmission over the internet is 100% secure.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">8. YOUR RIGHTS</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  You have the right to:
                </p>
                <ul className="mt-3 space-y-1 pl-4 list-disc list-inside">
                  <li className="font-courier text-sm text-ink/70">Access the personal information we hold about you</li>
                  <li className="font-courier text-sm text-ink/70">Request correction of inaccurate information</li>
                  <li className="font-courier text-sm text-ink/70">Request deletion of your personal information</li>
                  <li className="font-courier text-sm text-ink/70">Opt out of SMS communications</li>
                </ul>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">9. CHANGES TO THIS POLICY</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised "Last updated" date.
                </p>
              </div>

              <div>
                <h2 className="font-anton text-lg tracking-wider text-ink">10. CONTACT US</h2>
                <p className="mt-3 font-courier text-sm text-ink/70 leading-relaxed">
                  If you have questions about this Privacy Policy or wish to exercise your rights, please contact the property manager through the guest portal at{" "}
                  <a href="https://hostbase.app" target="_blank" rel="noopener noreferrer" className="text-teal underline">hostbase.app</a>{" "}
                  or visit us at{" "}
                  <a href="https://seasthebay.org" target="_blank" rel="noopener noreferrer" className="text-teal underline">seasthebay.org</a>{" "}
                  or reach out to us at stay@seasthebay.org.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
